import { useRef, useState, useEffect } from "react";

// ─── Intersection Hook ────────────────────────────────────────────────────────
function useIntersect(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// ─── FadeIn Wrapper ───────────────────────────────────────────────────────────
function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useIntersect();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Images ───────────────────────────────────────────────────────────────────
const IMAGES = {
  overview: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1400&q=85",
  living:   "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1400&q=85",
  bedroom:  "https://images.unsplash.com/photo-1560448204-603b3fc33ddc?w=1400&q=85",
  kitchen:  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=900&q=85",
  study:    "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=900&q=85",
  closet:   "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=900&q=85",
  tv:       "https://images.unsplash.com/photo-1593359677879-a4bb92f4834e?w=900&q=85",
};

const HERO_IMGS = [IMAGES.overview, IMAGES.living, IMAGES.bedroom];

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#overview",  label: "Overview"  },
    { href: "#amenities", label: "Amenities" },
    { href: "#units",     label: "Units"     },
    { href: "#gallery",   label: "Gallery"   },
    { href: "#location",  label: "Location"  },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(255,255,255,0.97)" : "transparent",
        boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.08)" : "none",
        backdropFilter: scrolled ? "blur(8px)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex items-center gap-2 no-underline">
          <span
            className="font-serif font-bold text-lg tracking-widest"
            style={{ color: scrolled ? "#991b1b" : "#fff" }}
          >
            GOYAL ORCHID
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[11px] font-bold tracking-[0.18em] no-underline transition-colors duration-200"
              style={{ color: scrolled ? "#4b5563" : "rgba(255,255,255,0.8)" }}
              onMouseEnter={e => e.target.style.color = scrolled ? "#991b1b" : "#fff"}
              onMouseLeave={e => e.target.style.color = scrolled ? "#4b5563" : "rgba(255,255,255,0.8)"}
            >
              {l.label}
            </a>
          ))}
          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 bg-red-700 hover:bg-red-800 text-white text-[11px] font-bold tracking-[0.18em] rounded-md no-underline transition-colors duration-200"
          >
            ENQUIRE NOW
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden p-2 bg-transparent border-none cursor-pointer"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle menu"
        >
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="rounded-sm transition-all duration-300"
              style={{
                width: 20, height: 2,
                marginBottom: i < 2 ? 4 : 0,
                background: scrolled ? "#1f2937" : "#fff",
                transform: menuOpen
                  ? i === 0 ? "rotate(45deg) translate(4px, 4px)"
                  : i === 1 ? "scaleX(0)"
                  : "rotate(-45deg) translate(4px, -4px)"
                  : "none",
                opacity: menuOpen && i === 1 ? 0 : 1,
              }}
            />
          ))}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="bg-white border-t border-gray-100 shadow-lg">
          <div className="flex flex-col px-4 py-3 gap-2">
            {links.map(l => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="text-gray-700 text-sm font-semibold tracking-wide py-1 no-underline"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://wa.me/message/MDG2ZSZNG6P6G1"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="mt-1 py-2.5 bg-red-700 text-white text-[11px] font-bold tracking-[0.18em] rounded-md text-center no-underline"
            >
              ENQUIRE NOW
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % HERO_IMGS.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Slideshow Backgrounds */}
      {HERO_IMGS.map((src, i) => (
        <div
          key={i}
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
          style={{ backgroundImage: `url(${src})`, opacity: i === idx ? 1 : 0 }}
        />
      ))}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />

      {/* Decorative Borders */}
      <div className="absolute inset-6 border border-white/20 pointer-events-none hidden md:block" />
      <div className="absolute inset-10 border border-red-800/30 pointer-events-none hidden md:block" />

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <div className="inline-block border border-red-300/60 text-red-300 text-[11px] tracking-[0.4em] px-6 py-2 rounded-sm mb-6 uppercase">
          Whitefield · Bengaluru
        </div>
        <h1
          className="font-serif font-bold text-white leading-none mb-2"
          style={{ fontSize: "clamp(3rem,10vw,6rem)", letterSpacing: "-0.02em" }}
        >
          GOYAL ORCHID
        </h1>
        <h2
          className="font-serif font-light text-red-400 tracking-[0.2em] mb-6"
          style={{ fontSize: "clamp(1.8rem,6vw,3.5rem)" }}
        >
          WHITEFIELD
        </h2>
        <p
          className="text-white/70 tracking-[0.18em] font-light max-w-md mx-auto mb-10"
          style={{ fontSize: "clamp(0.85rem,2vw,1.1rem)" }}
        >
          Refined Living · Fully Furnished · Timeless Sophistication
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3.5 bg-red-700 hover:bg-red-800 text-white font-bold tracking-[0.18em] text-sm rounded-md no-underline transition-colors duration-200 shadow-[0_8px_32px_rgba(153,27,27,0.5)]"
          >
            REQUEST CALLBACK
          </a>
          <a
            href="#overview"
            className="px-8 py-3.5 border border-white/40 hover:border-white text-white font-semibold tracking-[0.18em] text-sm rounded-md no-underline transition-colors duration-200"
          >
            EXPLORE PROJECT
          </a>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {HERO_IMGS.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            className="h-2 rounded-full border-none cursor-pointer transition-all duration-300 p-0"
            style={{
              width: i === idx ? 24 : 8,
              background: i === idx ? "#ef4444" : "rgba(255,255,255,0.4)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────
function Stats() {
  return (
    <div className="bg-red-700 py-4 px-4">
      <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "LOCATION",    value: "Whitefield"   },
          { label: "FLOOR",       value: "18th Floor"   },
          { label: "SIZE",        value: "1,160 sq.ft." },
          { label: "MAINTENANCE", value: "50+ Items"    },
        ].map(s => (
          <div key={s.label} className="text-center py-2">
            <div className="text-white font-bold text-xl">{s.value}</div>
            <div className="text-red-200 text-[10px] tracking-[0.2em] mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── About / Overview ─────────────────────────────────────────────────────────
function About() {
  const cards = [
    { icon: "🏰", title: "Fully Furnished Homes", desc: "Every unit is delivered fully furnished with premium fittings, modern furniture, and 50+ maintenance items — move-in ready from day one." },
    { icon: "🌿", title: "Orchid Green Living",   desc: "A tranquil residential environment designed to refresh your mind and body, surrounded by lush greenery and thoughtful community spaces." },
    { icon: "🏆", title: "Legacy Developer",      desc: "Founded in 1971 by Mr. Rampurshottam Goyal, Goyal & Co. and Hariyana Group have redefined Bengaluru's skyline — now elevating luxury living with Orchid." },
  ];

  return (
    <section id="overview" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="text-center mb-16">
            <p className="text-red-600 text-[11px] tracking-[0.4em] uppercase mb-3">About The Project</p>
            <h2 className="font-serif font-bold text-gray-900 mb-4" style={{ fontSize: "clamp(2rem,5vw,3rem)" }}>
              Premium Living,{" "}
              <span className="text-red-700">Fully Furnished &amp; Ready</span>
            </h2>
            <div className="w-16 h-0.5 bg-red-600 mx-auto mb-6" />
            <p className="text-gray-500 max-w-xl mx-auto leading-7 text-sm sm:text-base">
              Goyal Orchid Whitefield offers exclusive fully furnished 2 BHK and 3 BHK homes in the heart of Whitefield, Bengaluru. Crafted by Goyal &amp; Co., a legacy developer since 1971, every detail reflects quality, comfort, and refined urban living at its finest.
            </p>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <FadeIn key={c.title} delay={i * 0.1}>
              <div
                className="p-8 border border-gray-100 rounded-2xl bg-white h-full transition-all duration-300 cursor-default hover:shadow-xl hover:border-red-100"
              >
                <div className="text-4xl mb-5">{c.icon}</div>
                <h3 className="font-serif font-bold text-gray-900 text-lg mb-3">{c.title}</h3>
                <p className="text-gray-500 text-sm leading-7">{c.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Amenities ────────────────────────────────────────────────────────────────
const AMENITIES = [
  { icon: "🛋️", label: "Fully Furnished"   },
  { icon: "❄️", label: "Air Conditioning"  },
  { icon: "📺", label: "Smart TV"          },
  { icon: "🍳", label: "Modular Kitchen"   },
  { icon: "🧺", label: "Washing Machine"   },
  { icon: "🛏️", label: "Premium Beds"      },
  { icon: "🚿", label: "Modern Bathrooms"  },
  { icon: "🔒", label: "24/7 Security"     },
  { icon: "🏋️", label: "Outdoor Gym"       },
  { icon: "🌊", label: "Lily Pond"         },
  { icon: "⛳", label: "Mini Golf"         },
  { icon: "🧘", label: "Yoga Pavilion"     },
  { icon: "⛹️", label: "Basketball Court"  },
  { icon: "👥", label: "Social Square"     },
  { icon: "🏓", label: "Pickleball Court"  },
  { icon: "🚗", label: "Valet Parking"     },
];

function AmenityCard({ icon, label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="rounded-xl p-4 text-center transition-all duration-300 cursor-default border"
      style={{
        background: hovered ? "#b91c1c" : "#fff",
        borderColor: hovered ? "#b91c1c" : "#f3f4f6",
      }}
    >
      <div className="text-2xl mb-2">{icon}</div>
      <div
        className="text-[11px] font-semibold tracking-wide leading-tight"
        style={{ color: hovered ? "#fff" : "#4b5563" }}
      >
        {label}
      </div>
    </div>
  );
}

function Amenities() {
  return (
    <section id="amenities" className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="text-center mb-14">
            <p className="text-red-600 text-[11px] tracking-[0.4em] uppercase mb-3">Lifestyle Amenities</p>
            <h2 className="font-serif font-bold text-gray-900 mb-2" style={{ fontSize: "clamp(2rem,5vw,3rem)" }}>
              50+ Curated <span className="text-red-700">Amenities</span>
            </h2>
            <div className="w-16 h-0.5 bg-red-600 mx-auto my-4" />
            <p className="text-gray-500 text-sm max-w-md mx-auto leading-7">
              Every detail thoughtfully included — from premium furnishings inside your home to world-class lifestyle facilities across the community.
            </p>
          </div>
        </FadeIn>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {AMENITIES.map((a, i) => (
            <FadeIn key={a.label} delay={i * 0.04}>
              <AmenityCard icon={a.icon} label={a.label} />
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Unit Types ───────────────────────────────────────────────────────────────
const UNITS = [
  {
    name: "2 BHK", area: "1,160 sq.ft.", icon: "🌙", tag: "Cozy",
    price: "Starts from ₹50,000/mo",
    desc: "A thoughtfully designed 2 BHK unit on the 18th floor offering sweeping city views. Fully furnished with premium interiors, modern appliances, and 50+ maintenance items included.",
    highlights: ["Fully Furnished", "18th Floor Views", "50+ Maintenance Items", "Premium Finishes"],
  },
  {
    name: "3 BHK", area: "Spacious Layout", icon: "☀️", tag: "Spacious",
    price: "Starts from ₹60,000/mo",
    desc: "An expansive 3 BHK residence offering generous living space and premium furnishing throughout. Ideal for families seeking comfort, style, and a prime Whitefield address.",
    highlights: ["Fully Furnished", "Premium Interiors", "50+ Maintenance Items", "Modern Luxury Design"],
  },
];

function UnitCard({ unit }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative overflow-hidden rounded-3xl p-8 flex flex-col transition-all duration-300 cursor-default"
      style={{
        border: `2px solid ${hovered ? "#f87171" : "#f3f4f6"}`,
        background: "linear-gradient(135deg, #fff 0%, #f9fafb 100%)",
        boxShadow: hovered ? "0 20px 60px rgba(0,0,0,0.12)" : "none",
      }}
    >
      {/* Animated top bar */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 origin-left transition-transform duration-300"
        style={{
          background: "linear-gradient(90deg, #b91c1c, #ef4444)",
          transform: hovered ? "scaleX(1)" : "scaleX(0)",
        }}
      />

      <div className="flex justify-between items-start mb-6">
        <span className="text-5xl">{unit.icon}</span>
        <span className="text-[11px] font-bold tracking-[0.15em] text-red-600 bg-red-50 border border-red-200 px-3.5 py-1.5 rounded-full uppercase">
          {unit.tag}
        </span>
      </div>

      <h3 className="font-serif font-bold text-gray-900 text-2xl mb-1">{unit.name}</h3>
      <div className="text-red-700 font-bold text-lg mb-1">{unit.area}</div>
      <div className="text-gray-400 text-xs font-semibold tracking-[0.15em] mb-4">{unit.price}</div>
      <p className="text-gray-500 text-sm leading-7 mb-6">{unit.desc}</p>

      <div className="mb-6 p-4 bg-white rounded-xl border border-gray-100">
        <p className="text-[11px] font-bold text-gray-700 tracking-[0.15em] uppercase mb-3">Key Highlights</p>
        <div className="grid grid-cols-2 gap-2">
          {unit.highlights.map((h, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-red-600 font-bold text-base leading-tight mt-0.5">•</span>
              <span className="text-gray-700 text-xs">{h}</span>
            </div>
          ))}
        </div>
      </div>

      <a
        href="https://wa.me/message/MDG2ZSZNG6P6G1"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-auto flex items-center justify-center gap-2 py-3 px-4 bg-red-700 hover:bg-red-800 text-white text-xs font-bold tracking-[0.18em] uppercase rounded-lg no-underline transition-colors duration-200"
      >
        GET A CALLBACK <span className="text-base">→</span>
      </a>
    </div>
  );
}

function UnitTypes() {
  return (
    <section id="units" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="text-center mb-14">
            <p className="text-red-600 text-[11px] tracking-[0.4em] uppercase mb-3">Unit Types</p>
            <h2 className="font-serif font-bold text-gray-900" style={{ fontSize: "clamp(2rem,5vw,3rem)" }}>
              Find Your <span className="text-red-700">Perfect Home</span>
            </h2>
            <div className="w-16 h-0.5 bg-red-600 mx-auto mt-4" />
          </div>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {UNITS.map((unit, i) => (
            <FadeIn key={unit.name} delay={i * 0.1}>
              <UnitCard unit={unit} />
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
const GALLERY_IMGS = [
  { src: IMAGES.overview, label: "Grand Exterior"     },
  { src: IMAGES.living,   label: "Living Spaces"      },
  { src: IMAGES.bedroom,  label: "Master Bedroom"     },
  { src: IMAGES.kitchen,  label: "Modern Kitchen"     },
  { src: IMAGES.study,    label: "Study Area"         },
  { src: IMAGES.closet,   label: "Walk-in Closet"     },
  { src: IMAGES.tv,       label: "Entertainment Zone" },
];

function Gallery() {
  return (
    <section id="gallery" className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <FadeIn>
          <div className="text-center mb-10">
            <p className="text-red-600 text-[11px] tracking-[0.4em] uppercase mb-3">Photo Gallery</p>
            <h2 className="font-serif font-bold text-gray-900" style={{ fontSize: "clamp(2rem,5vw,3rem)" }}>
              Inside <span className="text-red-700">Goyal Orchid</span>
            </h2>
            <div className="w-16 h-0.5 bg-red-600 mx-auto mt-4" />
          </div>
        </FadeIn>

        {/* Featured */}
        <FadeIn>
          <div className="relative overflow-hidden rounded-2xl mb-3 cursor-pointer group" style={{ aspectRatio: "16/7" }}>
            <img
              src={GALLERY_IMGS[0].src}
              alt={GALLERY_IMGS[0].label}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 text-white text-xs font-semibold tracking-[0.2em] uppercase">
              {GALLERY_IMGS[0].label}
            </div>
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-800/80 flex items-center justify-center text-white text-xs font-bold">1</div>
          </div>
        </FadeIn>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {GALLERY_IMGS.slice(1).map((img, i) => (
            <FadeIn key={i} delay={(i + 1) * 0.08}>
              <div className="relative overflow-hidden rounded-xl cursor-pointer group aspect-square">
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 text-white text-[10px] font-semibold tracking-[0.12em] uppercase">
                  {img.label}
                </div>
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-red-800/80 flex items-center justify-center text-white text-[10px] font-bold">
                  {i + 2}
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Location ─────────────────────────────────────────────────────────────────
function Location() {
  const points = [
    "5 min to Whitefield (Kadugodi) Metro Station",
    "Close to major IT parks & tech corridors",
    "Easy access to ITPL, Marathahalli & ORR",
    "Near top schools, hospitals & malls",
    "Well-connected via NICE Road & NH48",
  ];

  return (
    <section id="location" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <FadeIn>
          <div>
            <p className="text-red-600 text-[11px] tracking-[0.4em] uppercase mb-3">Prime Location</p>
            <h2 className="font-serif font-bold text-gray-900 mb-2" style={{ fontSize: "clamp(1.8rem,4vw,2.5rem)" }}>
              Whitefield,<br /><span className="text-red-700">Bengaluru</span>
            </h2>
            <div className="w-16 h-0.5 bg-red-600 mb-6" />
            <p className="text-gray-500 text-sm leading-7 mb-8">
              Situated in the heart of Whitefield — Bengaluru's leading IT and lifestyle hub — Goyal Orchid ensures seamless access to key destinations and premium connectivity.
            </p>
            <div className="flex flex-col gap-3 mb-8">
              {points.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-red-600" />
                  </div>
                  <span className="text-gray-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
            <a
              href="https://maps.app.goo.gl/S6tggjfe7oYjHyA89"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-700 hover:bg-red-800 text-white text-xs font-bold tracking-[0.16em] uppercase rounded-lg no-underline transition-colors duration-200"
            >
              📍 VIEW ON GOOGLE MAPS →
            </a>
          </div>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-2xl">
            <iframe
              title="Goyal Orchid Whitefield Location"
              src="https://maps.google.com/maps?q=Goyal+Orchid+Whitefield+Bengaluru&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="420"
              className="border-0 block"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────────
function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", type: "2 BHK" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = () => {
    const text = `Hi, I'm interested in Riviera North Edition Whitefield.\nName: ${form.name || "-"}\nPhone: ${form.phone || "-"}\nUnit Type: ${form.type || "-"}`;
    window.open(`https://wa.me/918088687429?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    setSubmitted(true);
  };

  return (
    <section id="contact" className="py-20 px-4 bg-red-700 relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full border border-white/10 pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full border border-white/10 pointer-events-none" />

      <div className="max-w-2xl mx-auto relative z-10">
        <FadeIn>
          <div className="text-center mb-10">
            <p className="text-red-200 text-[11px] tracking-[0.4em] uppercase mb-3">Get In Touch</p>
            <h2 className="font-serif font-bold text-white mb-4" style={{ fontSize: "clamp(1.8rem,4vw,2.5rem)" }}>
              Schedule a Site Visit
            </h2>
            <p className="text-red-200 text-sm max-w-md mx-auto leading-7">
              Interested in Goyal Orchid Whitefield? Leave your details and our team will call you back to arrange a visit or answer any queries.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          {submitted ? (
            <div className="bg-white/10 border border-white/25 rounded-2xl p-14 text-center backdrop-blur-sm">
              <div className="text-5xl mb-5">✅</div>
              <h3 className="font-serif text-white text-xl font-bold mb-2">Thank You!</h3>
              <p className="text-red-200 text-sm">We've opened WhatsApp. Our team will assist you shortly.</p>
              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 px-6 py-2.5 bg-white text-red-700 font-bold text-xs uppercase tracking-widest rounded-lg"
              >
                Reopen WhatsApp ↗
              </a>
            </div>
          ) : (
            <div className="bg-white/10 border border-white/20 rounded-2xl p-8 backdrop-blur-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { label: "Your Name",    name: "name",  type: "text", placeholder: "John Doe"         },
                  { label: "Phone Number", name: "phone", type: "tel",  placeholder: "+91 98765 43210" },
                ].map(f => (
                  <div key={f.name}>
                    <label className="block text-red-200 text-[11px] tracking-[0.2em] uppercase mb-2">{f.label}</label>
                    <input
                      type={f.type}
                      name={f.name}
                      value={form[f.name]}
                      onChange={e => { handleChange(e); setErrors(p => ({ ...p, [f.name]: false })); }}
                      placeholder={f.placeholder}
                      className="w-full bg-white/10 text-white placeholder-white/40 rounded-lg px-4 py-3 text-sm outline-none focus:bg-white/15 transition-colors border"
                      style={{ borderColor: errors[f.name] ? "#fde68a" : "rgba(255,255,255,0.3)" }}
                    />
                    {errors[f.name] && (
                      <p className="text-yellow-300 text-xs mt-1">This field is required</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <label className="block text-red-200 text-[11px] tracking-[0.2em] uppercase mb-2">Interested In</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/30 text-white rounded-lg px-4 py-3 text-sm outline-none focus:bg-white/15 transition-colors"
                >
                  <option value="2 BHK" className="text-gray-900">2 BHK – Starts from ₹50,000/mo</option>
                  <option value="3 BHK" className="text-gray-900">3 BHK – Starts from ₹60,000/mo</option>
                </select>
              </div>

              <button
                onClick={handleSubmit}
                className="w-full py-4 bg-white hover:bg-red-50 text-red-700 font-bold text-sm tracking-[0.18em] uppercase rounded-lg border-none cursor-pointer transition-colors duration-200"
              >
                REQUEST CALLBACK →
              </button>
            </div>
          )}
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <div className="bg-gray-950 py-5 px-4 text-center">
      <div className="font-serif text-white text-sm tracking-[0.18em] mb-1">GOYAL ORCHID WHITEFIELD</div>
      <div className="text-gray-500 text-xs">Whitefield, Bengaluru · By Goyal &amp; Co. since 1971</div>
    </div>
  );
}

// ─── ROOT EXPORT ──────────────────────────────────────────────────────────────
export default function RiveraNorthEdition() {
  return (
    <div className="font-sans antialiased text-gray-900">
      <Navbar />
      <Hero />
      <Stats />
      <About />
      <Amenities />
      <UnitTypes />
      <Gallery />
      <Location />
      <Contact />
      <Footer />
    </div>
  );
}